import React, { FC, useState, useRef, useEffect } from 'react'
import { KTIcon } from '../../../_payairo/helpers'

export type DatePreset = 'last_24_hours' | 'last_7_days' | 'last_30_days' | 'last_month' | 'last_year' | 'custom'

interface DashboardDateFilterProps {
  preset: DatePreset
  onPresetChange: (value: DatePreset) => void
  customStart: string
  customEnd: string
  onCustomChange: (start: string, end: string) => void
  allowCustom?: boolean
}

const PRESET_LABELS: Record<DatePreset, string> = {
  last_24_hours: 'Last 24 Hours',
  last_7_days: 'Last 7 Days',
  last_30_days: 'Last 30 Days',
  last_month: 'Last Month',
  last_year: 'Last Year',
  custom: 'Custom Range'
}

const DashboardDateFilter: FC<DashboardDateFilterProps> = ({
  preset,
  onPresetChange,
  customStart,
  customEnd,
  onCustomChange,
  allowCustom = true,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Guard against stale "custom" state on pages where custom range is disabled.
  useEffect(() => {
    if (!allowCustom && preset === 'custom') {
      onPresetChange('last_30_days')
      onCustomChange('', '')
    }
  }, [allowCustom, preset, onPresetChange, onCustomChange])

  const handleSelect = (value: DatePreset) => {
    onPresetChange(value)
    if (value !== 'custom') {
      setIsOpen(false)
    }
  }

  return (
    <div className='cs-date-filter' ref={dropdownRef} style={{ position: 'relative' }}>
      <button 
        className='cs-select cs-date-filter-select'
        onClick={() => setIsOpen(!isOpen)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%' }}
      >
        <span className='truncate'>{PRESET_LABELS[preset]}</span>
        <KTIcon iconName={isOpen ? 'up' : 'down'} className='fs-5' />
      </button>

      {isOpen && (
        <div className='cs-custom-dropdown-menu'>
          {(Object.keys(PRESET_LABELS) as DatePreset[])
            .filter((key) => allowCustom || key !== 'custom')
            .map((key) => (
            <div 
              key={key}
              className={`cs-custom-dropdown-item ${preset === key ? 'active' : ''}`}
              onClick={() => handleSelect(key)}
            >
              {PRESET_LABELS[key]}
            </div>
          ))}
        </div>
      )}

      {allowCustom && preset === 'custom' && (
        <div className='cs-date-filter-range' style={{ marginTop: '10px' }}>
          <input
            type='date'
            className='cs-input cs-date-input'
            value={customStart}
            onChange={(e) => onCustomChange(e.target.value, customEnd)}
          />
          <span className='cs-date-separator'>–</span>
          <input
            type='date'
            className='cs-input cs-date-input'
            value={customEnd}
            onChange={(e) => onCustomChange(customStart, e.target.value)}
          />
        </div>
      )}
    </div>
  )
}

export default DashboardDateFilter


